import { PropsWithChildren } from "react";

import Loader from "@/components/loader";
import { useCurrentLeagueContext, useCurrentOrganizationContext } from "@/contexts";

/**
 * Holds dashboard page content behind a loader while the organization / league
 * capability sets are still resolving.
 *
 * Under strict RBAC every page gate denies on an empty capability set, and the
 * org / league sets arrive with their `available` / `refs` record — so rendering
 * a page before its scope resolves would flash a Forbidden / NotFound state (the
 * `enabled: !!id && canX` detail fetches also short-circuit while `canX` is still
 * false). The platform set is already guaranteed resolved by
 * `SplashScreenProvider`, so gating here on the remaining two contexts means
 * every page gate only ever evaluates against a **resolved** set: an empty set
 * then means a real denial, not a load in progress.
 *
 * Platform-only mode (no org) and non-league routes report `loading: false`, so
 * they fall straight through. See docs/capabilities.md ("Loading semantics").
 */
export const DashboardScopeGate = ({ children }: PropsWithChildren) => {
  const { loading: organizationLoading } = useCurrentOrganizationContext();
  const { loading: leagueLoading } = useCurrentLeagueContext();

  if (organizationLoading || leagueLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return <>{children}</>;
};
